<template>
    <v-form
        ref="form"
        @submit.prevent="onSubmit">
            <v-container fluid>
                <v-row>
                    <v-col cols="12" lg="6" class="pr-lg-15">

                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Markup Settings
                        </v-subheader>

                        <v-select
                            v-model="form.margin_type"
                            :items="marginTypes"
                            label="Margin Types">
                        </v-select>


                        <v-text-field v-if="form.margin_type != 'n'"
                            v-model="form.amount"
                            type="number"
                            :label="form.margin_type == 'AUD' ? 'Amount (AUD)' : 'Percentage (%)'"
                            >
                        </v-text-field>

                        <v-radio-group v-model="form.price_return_type" row label="Price return type:">
                            <v-radio label="Inc GST" value="inc"></v-radio>
                            <v-radio label="Exc. GST" value="exc"></v-radio>
                        </v-radio-group>


                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Minimum Shipping Settings
                        </v-subheader>
                        <v-text-field
                            v-model="form.minimum_shipping_cost"
                            type="number"
                            label="Minimum Shipping Cost">
                        </v-text-field>

                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Rounding Settings
                        </v-subheader>
                        <v-radio-group v-model="form.rounding">
                            <v-radio label="No Rounding" value="0"></v-radio>
                            <v-radio label="Rounding Up" value="up"></v-radio>
                            <v-radio label="Rounding Down" value="down"></v-radio>
                        </v-radio-group>

                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Transit Settings
                        </v-subheader>
                        <p>Hide delivery date estimate?</p>
                        <v-radio-group v-model="form.is_hide_delivery" row>
                            <v-radio label="yes" value="1"></v-radio>
                            <v-radio label="no" value="0"></v-radio>
                        </v-radio-group>

                        <v-text-field v-if="form.is_hide_delivery == 0"
                            v-model="form.add_transit_days"
                            type="number"
                            label="Add Transit Days">
                        </v-text-field>

                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Slow Rate Alerts
                        </v-subheader>

                        <v-switch
                            v-model="form.slow_alert_enabled"
                            label="Email me when live rates take longer than 10 seconds"
                            color="primary"
                            class="mt-0"
                            dense
                            hint="Throttled to at most one email every 5 minutes per integration."
                            persistent-hint>
                        </v-switch>

                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Request Timeout
                        </v-subheader>

                        <v-text-field
                            v-model.number="form.primary_timeout_seconds"
                            type="number"
                            label="Primary timeout (seconds)"
                            hint="Leave blank to use the default. Cart-side timeouts typically fire at 10s (Shopify carrier service)."
                            persistent-hint
                            min="1"
                            class="mb-4">
                        </v-text-field>

                        <v-subheader class="text-h6 font-weight-bold px-0 mt-4 mb-2">
                            Enable Retry
                        </v-subheader>

                        <v-switch
                            v-model="form.add_carrier_retry_enabled"
                            label="Retry once when MachShip times out"
                            color="primary"
                            class="mt-0"
                            dense>
                        </v-switch>

                        <template v-if="form.add_carrier_retry_enabled">

                            <v-text-field
                                v-model.number="form.add_carrier_retry_timeout_seconds"
                                type="number"
                                label="Retry timeout (seconds)"
                                persistent-hint
                                hint="Time to wait for the retry response before falling back. Keep this + primary timeout under your cart-side budget."
                                min="1"
                                class="mt-2 mb-6">
                            </v-text-field>

                            <v-radio-group
                                v-model="form.add_carrier_retry_mode"
                                mandatory
                                dense
                                hide-details
                                class="mt-0 mb-4">
                                <template v-slot:label>
                                    <span class="text-body-2 font-weight-medium">Retry carrier list mode</span>
                                </template>
                                <v-radio label="Use only these carriers" value="override"></v-radio>
                                <v-radio label="Use all carriers except these" value="exclude"></v-radio>
                            </v-radio-group>

                            <CarrierMultiSelect
                                v-if="id"
                                v-model="form.add_carrier_retry_carriers"
                                :integration_id="id" />

                            <v-alert dense outlined type="info" class="mt-4">
                                Worst-case wait: {{ worstCaseSeconds }}s. Cart timeouts typically fire at 10s &mdash; keep primary + retry under your cart-side budget.
                            </v-alert>
                        </template>

                        <div class="d-flex">
                        <v-spacer></v-spacer>
                        <v-btn :disabled="isSaving" small type="submit" color="primary">
                            <span v-if="isSaving">
                                <v-progress-circular
                                    indeterminate
                                    size="12"
                                    width="2"
                                    color="primary darken-3">
                                </v-progress-circular>
                                Saving..
                            </span>
                            <span v-else>Save Settings</span>
                        </v-btn>
                    </div>
                    </v-col>
                </v-row>

            </v-container>
    </v-form>
</template>

<script>

import { EventBus } from '@/js/event-bus.js'
import CarrierMultiSelect from './components/CarrierMultiSelect';

const target = '/api/live-rates/settings/fallback';
const requestTimeoutTarget = '/api/live-rates/settings/request-timeout';

export default {

    components: { CarrierMultiSelect },

    props: {
        id: { type: Number, default: null },
    },


    data() {
        return {
            form: {

                margin_type: 'n',
                amount: 0,
                minimum_shipping_cost: 0,
                rounding: '0',
                is_hide_delivery: 0,
                add_transit_days: 0,
                price_return_type: 'inc',
                primary_timeout_seconds: null,
                add_carrier_retry_enabled: false,
                add_carrier_retry_timeout_seconds: 8,
                add_carrier_retry_carriers: [],
                add_carrier_retry_mode: 'override',
                slow_alert_enabled: false,
            },
            marginTypes: [
                { text: 'Percentage (%)', value: '%' },
                { text: 'Fixed ($)', value: 'AUD' },
                { text: 'None', value: 'n' },
            ],
            isSaving: false
        }
    },

    computed: {
        worstCaseSeconds() {
            const primary = parseInt(this.form.primary_timeout_seconds, 10) || 0;
            const retry = parseInt(this.form.add_carrier_retry_timeout_seconds, 10) || 0;
            return primary + retry;
        },
    },

    created() {
        this.fetchFallbackSettings();
        this.fetchRequestTimeoutSettings();
    },

    methods: {
        onSubmit() {

            this.isSaving = true;

            const requestTimeoutPayload = {
                primary_timeout_seconds: this.form.primary_timeout_seconds,
                add_carrier_retry_enabled: this.form.add_carrier_retry_enabled,
                add_carrier_retry_timeout_seconds: this.form.add_carrier_retry_timeout_seconds,
                add_carrier_retry_carriers: (this.form.add_carrier_retry_carriers || [])
                    .map(v => parseInt(v, 10))
                    .filter(v => Number.isFinite(v) && v > 0),
                add_carrier_retry_mode: this.form.add_carrier_retry_mode === 'exclude' ? 'exclude' : 'override',
            };

            // Sequence the two POSTs (different endpoints under the hood). If
            // the request-timeout leg fails after fallback already persisted,
            // surface that explicitly so the operator knows only the second
            // half needs to be retried.
            this.$http
                .post(`${target}/${this.id}`, this.form)
                .then(() => this.$http
                    .post(`${requestTimeoutTarget}/${this.id}`, requestTimeoutPayload)
                    .catch(err => Promise.reject({ partial: true, original: err }))
                )
                .then(() => {
                    EventBus.$emit('show-success', 'Settings updated successfully.');
                })
                .catch(err => {
                    if (err && err.partial) {
                        EventBus.$emit('show-error', 'Markup/fallback settings saved, but the request-timeout/retry update failed. Adjust the timeout fields and Save again to retry just that half.');
                    } else {
                        EventBus.$emit('show-error', err);
                    }
                }).finally(() => {
                    this.isSaving = false;
                });
        },

        fetchFallbackSettings() {

            this.isSaving = true

            this.$http
                .get(`${target}/${this.id}`)
                .then(({ data }) => {
                    this.form = { ...this.form, ...data.data.quote, ...data.data.fallback_settings };
                    this.form.use_custom_fallback = data.data.use_custom_fallback ?? false;
                    this.form.invalid_loc_fallback_settings = {...this.form.invalid_loc_fallback_settings, ...data.data.invalid_loc_fallback_settings ?? {}};
                    this.form.slow_alert_enabled = !!data.data.slow_alert_enabled;
                }).catch(err => {
                    EventBus.$emit('show-error', err);
                }).finally(() => {
                    this.isSaving = false
                });
        },

        fetchRequestTimeoutSettings() {
            this.$http
                .get(`${requestTimeoutTarget}/${this.id}`)
                .then(({ data }) => {
                    const d = data.data || {};
                    this.form.primary_timeout_seconds = (d.primary_timeout_seconds !== null && d.primary_timeout_seconds !== '')
                        ? parseInt(d.primary_timeout_seconds, 10)
                        : null;
                    this.form.add_carrier_retry_enabled = !!d.add_carrier_retry_enabled;
                    this.form.add_carrier_retry_timeout_seconds = parseInt(d.add_carrier_retry_timeout_seconds, 10) || 8;
                    this.form.add_carrier_retry_carriers = (d.add_carrier_retry_carriers || []).map(Number);
                    this.form.add_carrier_retry_mode = d.add_carrier_retry_mode === 'exclude' ? 'exclude' : 'override';
                })
                .catch(err => EventBus.$emit('show-error', err));
        },
    },


}

</script>
